import React, { ReactEventHandler, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import GenericModal from '../../../components/GenericModal'
import CreateAndEditProject from './CreateAndEditProject'
import type { IDistributionSeries } from '../../../models/DistributionSeries'
import { Menu, MenuItem, IconButton, useTheme } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { DeleteProject } from './DeleteProject'
import { DistributionManager } from '../../../APIManager'
import { notification } from '../../../components/Notifications'
import { useNavigate } from 'react-router-dom'
import { red, shade } from '../../../colors'
import { type ProjectScopeEnum } from '../../../pages/ProjectPage'

interface DistributionSeriesTopBarActionProps {
  selectedProject: IDistributionSeries
  scope: ProjectScopeEnum
}

export const ProjectActions: React.FC<DistributionSeriesTopBarActionProps> = ({ selectedProject, scope }) => {
  const [isCreateModal, setIsCreateModal] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [isDeleteModal, setIsDeleteModal] = useState<boolean>(false)

  const theme = useTheme()

  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = (): void => {
    setAnchorEl(null)
  }

  const handleEditClick = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation()
    setIsCreateModal(true)
    handleMenuClose()
  }

  const handleDeleteClick = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation()
    setIsDeleteModal(true)
    handleMenuClose()
  }

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return await DistributionManager.deleteProject({ projectId: selectedProject.distributionSeriesId })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['projects', scope] })
      const projects = (queryClient.getQueryData(['projects', scope]) as IDistributionSeries[]) || []
      if (Array.isArray(projects) && projects.length > 0 && projects[0].distributionSeriesId) {
        navigate(`?projectId=${projects[0].distributionSeriesId}`)
      }
      notification(`Survey Project "${selectedProject.title}" deleted successfully`, { variant: 'success' })
      setIsDeleteModal(false)
    },
    meta: { slient: true },
    onError: (error) => {
      notification(error.message || 'Failed to delete the Survey Project', { variant: 'error' })
    }
  })

  return (
    <>
      <IconButton
        onClick={e => { e.stopPropagation(); handleMenuClick(e) }}
        disableRipple
        sx={{ padding: 0 }}
      >
        <MoreVertIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Menu anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          '& .MuiMenu-paper': {
            borderRadius: theme.spacing(2),
            width: theme.spacing(38),
            marginTop: theme.spacing(2),
            boxShadow: '0px 4px 6px -2px rgba(32, 41, 57, 0.03), 0px 12px 16px -4px rgba(32, 41, 57, 0.08)',
            border: `1px solid ${shade[650]}`
          }
        }}
      >
        {[
          <MenuItem key="edit" sx={{ fontSize: '14px', fontWeight: '500' }} onClick={handleEditClick}>Edit</MenuItem>,
          <MenuItem key="delete" sx={{ fontSize: '14px', fontWeight: '500', color: red[700] }} onClick={handleDeleteClick}>
            Delete
          </MenuItem>
        ]}
      </Menu>
      <GenericModal
        isOpen={isCreateModal}
        title={'Edit Survey Project'}
        onClose={(e?: React.MouseEvent<HTMLElement>) => {
          e?.stopPropagation()
          setIsCreateModal(false)
        }}
        maxWidth="sm"
        action={false}
      >
        <CreateAndEditProject selectedProject={selectedProject} handleCloseModal={() => { setIsCreateModal(false) }} />
      </GenericModal>
      <GenericModal
        isOpen={isDeleteModal}
        title={`Delete "${selectedProject?.title}"?`}
        onClose={() => {
          setIsDeleteModal(false)
          deleteMutation.reset()
        }}
        maxWidth="sm"
        action={false}
      >
        <DeleteProject
          distributionSeries={selectedProject}
          handleCloseModal={(e?: React.MouseEvent<HTMLElement>) => {
            e?.stopPropagation()
            setIsDeleteModal(false)
            // deleteMutation.reset()
          }}
          handleDeleteModal={() => {
            deleteMutation.mutate()
          }}
          loading={deleteMutation.isPending}
        />
      </GenericModal>
    </>
  )
}
